import { Button, Flex, Select } from '@gmg/gmg-react-components';
import { Controller, FieldValues, RegisterOptions, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Options } from 'src/components/SelectElementWrapper';
import styled from 'styled-components';
import { TargetLicenseSystemData } from '../LicenseManagementAction';

interface TransferLicenseSystemModalProps {
    targetLicenseSystems: Array<TargetLicenseSystemData>;
    sourceLicenseSystemId: string;
    onSubmit: (targetLicenseSystemId: string) => void;
    isSubmitting: boolean;
    onClose?: () => void;
};

export const TransferLicenseSystemModal = (props: TransferLicenseSystemModalProps) => {
    const { t } = useTranslation();
    const { handleSubmit, control, formState: { errors } } = useForm<{ targetLicenseSystem: Options }>({ mode: 'onChange' });

    const errorMessages = {
        requiredField: t('Validation.forms.field_required_val', 'Uh oh, please fill out this field.'),
        appServiceUpdateRequired: t('MoveLicensesModal.target_does_not_support_transfer_error_msg', 'Application service needs to be updated'),
        inactiveLicenseSystem: t('MoveLicensesModal.target_was_not_active_error_msg', 'This license system has been offline for more than 2 hours and cannot be used for transfer.'),
        targetLicenseSystemUnavailable: t('MoveLicensesModal.target_license_system_unavailable_error_msg', 'Selected target license system is no longer available.'),
        noAvailableTargetLicenseSystem: t('MoveLicensesModal.no_target_license_system_error_desc', 'Please add a target license system to your organization either by pairing an existing one through the local Application Service or creating a new one. For step-by-step instructions, feel free to check out our help center.'),
    };
    const areLicenseSystemsPresent = props.targetLicenseSystems.length > 0;
    const noLicenseSystemAvailableErrorMessage = !areLicenseSystemsPresent && errorMessages.noAvailableTargetLicenseSystem;

    const options: Array<Options> = props.targetLicenseSystems
        .map(({ id, userName, location }) => ({
            label: `${id} - ${userName} ${location !== '-' ? `(${location})` : ''}`,
            value: id,
        }));

    const validator: Pick<RegisterOptions<FieldValues>, 'validate' | 'required'> = {
        required: errorMessages.requiredField,
        validate: {
            isTransferSupportedByTarget: ({ value: selectedLsId }) => {
                const selectedSystem = props.targetLicenseSystems.find(ls => ls.id === selectedLsId);
                if (!selectedSystem) return errorMessages.targetLicenseSystemUnavailable;

                return selectedSystem.isTransferSupported || errorMessages.appServiceUpdateRequired;
            },
            isSystemActiveWithinLastTwoHours: ({ value: selectedLsId }) => {
                const selectedSystem = props.targetLicenseSystems.find(ls => ls.id === selectedLsId);
                if (!selectedSystem) return errorMessages.targetLicenseSystemUnavailable;

                const wasSystemActiveInTwoHours = wasSystemActiveWithinRange(selectedSystem.lastSeenDate, 2);
                return wasSystemActiveInTwoHours || errorMessages.inactiveLicenseSystem;
            },
        },
    };

    return (
        <form onSubmit={handleSubmit(data => {
            props.onSubmit(data.targetLicenseSystem.value);
        })}>
            <Flex flexDirection='column' alignItems='space-between'>

                <Flex flexDirection="column" gap="32px" height="300px" className="px12_400_normal">

                    <Flex flexDirection="column" gap="16px">
                        <Paragraph>
                            {t('MoveLicensesModal.modal_desc',
                                'Transfer all licenses to another computer - like unplugging a USB license dongle from one computer and plugging it into another (digital equivalent).',
                            )}
                        </Paragraph>
                        <Paragraph>
                            <Trans
                                i18nKey="MoveLicensesModal.modal_info_desc"
                                components={{ bold: <strong /> }}
                            />
                        </Paragraph>
                    </Flex>

                    {/* Set a fixed height to prevent the modal from resizing when displaying error messages */}
                    <Flex flexDirection="column" gap="8px" maxWidth="100%" height="50px">
                        <Paragraph className="px12_400_weak">
                            {t('LicenseManagementTable.columns.license_system_title', 'License system')}
                        </Paragraph>
                        <Controller
                            control={control}
                            name="targetLicenseSystem"
                            rules={validator}
                            render={({ field: { onChange, ref, value } }) => (
                                <Select
                                    onChange={onChange}
                                    ref={ref}
                                    options={options}
                                    value={value}
                                    placeholder={t('MoveLicensesModal.placeholder_desc', 'Select target license system')}
                                    name="targetLicenseSystem"
                                    width="100%"
                                    maxMenuHeight={130}
                                    error={noLicenseSystemAvailableErrorMessage || errors.targetLicenseSystem?.message}
                                />
                            )}
                        />
                    </Flex>

                </Flex>

                <Flex gap="16px" justifyContent="end">
                    <Button
                        variant="primary"
                        type="submit"
                        disabled={props.isSubmitting || !areLicenseSystemsPresent || !!errors.targetLicenseSystem}
                    >
                        {t('MoveLicensesModal.action_transfer_btn', 'Transfer')}
                    </Button>
                    <Button
                        variant="secondary"
                        type="button"
                        onClick={() => { props.onClose?.(); }}
                        aria-label={'cancel action'}
                    >
                        {t('ButtonTitles.action_cancel_btn', 'Cancel')}
                    </Button>
                </Flex>

            </Flex>
        </form>
    );
};

const Paragraph = styled.p`
    margin: 0;
    line-height: 1.4;
`;

const wasSystemActiveWithinRange = (date: string | undefined, numberOfhours: number) => {
    if (date === undefined) return false;

    const lastSeenDate = new Date(date).getTime();
    const currentDate = new Date().getTime();
    const rangeInMilliseconds = numberOfhours * 60 * 60 * 1000;

    return (currentDate - lastSeenDate) < rangeInMilliseconds;
};
